import React, { createContext, useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { UserContext } from './UserContext';

export const VeriffContext = createContext({
  veriffStatus: null,
  veriffUrl: null,
  veriffLoading: false,
  veriffMessage: null,
  veriffHasRecord: false,
  veriffData: null,
  openSession: (url) => {},
  refreshVeriff: async () => {},
});

const VeriffProvider = ({ children }) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { user } = useContext(UserContext);
  const [veriffStatus, setVeriffStatus] = useState(null);
  const [veriffUrl, setVeriffUrl] = useState(null);
  const [veriffLoading, setVeriffLoading] = useState(false);
  const [veriffMessage, setVeriffMessage] = useState(null);
  const [veriffHasRecord, setVeriffHasRecord] = useState(false);
  const [veriffData, setVeriffData] = useState(null);
  const hasFetchedRef = useRef(false);

  const fetchVeriff = useCallback(async () => {
    if (!user?.id) {
      setVeriffStatus(null);
      setVeriffUrl(null);
      setVeriffMessage(null);
      setVeriffHasRecord(false);
      setVeriffData(null);
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) return;
    setVeriffLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/user/veriff/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res?.data?.ok) {
        const d = res.data.data || null;
        setVeriffData(d);
        setVeriffMessage(res.data.message || null);
        if (!d) {
          setVeriffStatus(null);
          setVeriffUrl(null);
          setVeriffHasRecord(false);
        } else {
          setVeriffStatus(d.status || null);
          setVeriffUrl(d.url || null);
          setVeriffHasRecord(true);
        }
      }
    } catch (err) {
      // silent
    } finally {
      setVeriffLoading(false);
    }
  }, [user?.id, apiUrl]);

  // Lazy fetch: only call the API when a consumer explicitly triggers it
  // (instead of fetching on every app mount)
  const ensureFetched = useCallback(() => {
    if (!hasFetchedRef.current && user?.id) {
      hasFetchedRef.current = true;
      fetchVeriff();
    }
  }, [fetchVeriff, user?.id]);

  // Reset fetch flag when user changes
  useEffect(() => {
    hasFetchedRef.current = false;
  }, [user?.id]);

  const openSession = useCallback((url) => {
    if (!url) return;
    try {
      window.open(url, '_blank');
    } catch (e) {
      // fallback
      window.location.href = url;
    }
  }, []);

  const contextValue = useMemo(() => ({
    veriffStatus,
    veriffUrl,
    veriffLoading,
    veriffMessage,
    veriffHasRecord,
    veriffData,
    openSession,
    refreshVeriff: fetchVeriff,
    ensureFetched,
  }), [veriffStatus, veriffUrl, veriffLoading, veriffMessage, veriffHasRecord, veriffData, openSession, fetchVeriff, ensureFetched]);

  return (
    <VeriffContext.Provider value={contextValue}>
      {children}
    </VeriffContext.Provider>
  );
};

export default VeriffProvider;
